import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Stack
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading, setAlertData } from "../../Redux/Screen/Action";
import AdminApi from "Service/Admin.api";
import { StyledTableCell, StyledTableRow } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import { formatUserType } from "Utils/Common";

type CaseItem = {
  pkCase: number;
  caseNumber: string;
  description: string;
  casePrice: number;
  dateCreated: string;
  orderType: {
    pkOrderType: number;
    orderTypeName: string;
    orderPrice: number;
  };
  client: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
};

type AdminUserItem = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

const BulkAdminAssignment: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState<number[]>([]);
  const [admins, setAdmins] = useState<AdminUserItem[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<number | "">("");
  const [textSearch, setTextSearch] = useState<string>("");

  // Result Dialog State
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState<any[]>([]);

  useEffect(() => {
    fetchUnassignedCases();
    fetchAdmins();
  }, []);

  const fetchUnassignedCases = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Fetching unassigned admin cases..." }));
    try {
      const res = await AdminApi.getAllUnAssignedAdminCases();
      if (res?.success && Array.isArray(res.data)) {
        setCases(res.data);
      } else {
        setCases([]);
      }
    } catch (error) {
      console.error("Error fetching unassigned admin cases:", error);
      dispatch(
        setAlertData({
          alertMessage: "Failed to fetch unassigned admin cases.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await AdminApi.getAllUsers("Admin", 1, 100);
      if (res?.success) {
        const adminData = res.data?.data || res.data || [];
        if (Array.isArray(adminData)) {
          setAdmins(adminData);
        }
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  // Filter cases on clientside based on textSearch
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      const q = textSearch.toLowerCase().trim();
      if (!q) return true;

      const caseNo = c.caseNumber?.toLowerCase() || "";
      const clientName = `${c.client?.firstName || ""} ${c.client?.lastName || ""}`.toLowerCase();
      const clientEmail = c.client?.email?.toLowerCase() || "";
      const desc = c.description?.toLowerCase() || "";
      const orderType = c.orderType?.orderTypeName?.toLowerCase() || "";

      return caseNo.includes(q) || clientName.includes(q) || clientEmail.includes(q) || desc.includes(q) || orderType.includes(q);
    });
  }, [cases, textSearch]);

  const handleSelectCase = (caseId: number) => {
    setSelectedCaseIds((prev) =>
      prev.includes(caseId) ? prev.filter((id) => id !== caseId) : [...prev, caseId]
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = filteredCases.map((c) => Number(c.pkCase));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds([]);
    }
  };

  const handleAssign = async () => {
    if (!selectedAdminId || selectedCaseIds.length === 0) return;

    dispatch(startLoading({ loading: true, loadtext: `Assigning ${selectedCaseIds.length} cases...` }));
    try {
      const res = await AdminApi.assignAdminBatch({
        adminId: Number(selectedAdminId),
        caseIds: selectedCaseIds
      });

      if (res?.success) {
        setAssignmentResults(res.results || []);
        setResultDialogOpen(true);

        // Refresh case list by removing successfully assigned cases
        const successfulCaseIds = (res.results || [])
          .filter((r: any) => r.success)
          .map((r: any) => Number(r.caseId));

        setCases((prevCases) =>
          prevCases.filter((c) => !successfulCaseIds.includes(Number(c.pkCase)))
        );
        setSelectedCaseIds([]);
        setSelectedAdminId("");
      } else {
        dispatch(
          setAlertData({
            alertMessage: res?.message || "Failed to process bulk assignment.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (error: any) {
      console.error("Bulk assignment error:", error);
      dispatch(
        setAlertData({
          alertMessage: error?.message || "An error occurred during assignment.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2, color: ColorData.primary }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight="bold" color="primary">
          Bulk Admin Assignment
        </Typography>
      </Box>

      {/* Grid: Cases and Assignment Controls */}
      <Grid container spacing={3}>
        {/* Left Side: Cases List */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
              <Typography variant="h6" fontWeight="bold">
                Unassigned Cases ({filteredCases.length})
              </Typography>
              <TextField
                placeholder="Search cases..."
                size="small"
                value={textSearch}
                onChange={(e) => setTextSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Box>

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedCaseIds.length > 0 && selectedCaseIds.length < filteredCases.length}
                        checked={filteredCases.length > 0 && selectedCaseIds.length === filteredCases.length}
                        onChange={handleSelectAll}
                      />
                    </StyledTableCell>
                    <StyledTableCell>Case Number</StyledTableCell>
                    <StyledTableCell>Order Type</StyledTableCell>
                    <StyledTableCell>Client</StyledTableCell>
                    <StyledTableCell>Date Created</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No unassigned admin cases found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCases.map((c) => (
                      <StyledTableRow
                        key={c.pkCase}
                        hover
                        onClick={() => handleSelectCase(Number(c.pkCase))}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCaseIds.includes(Number(c.pkCase))}
                            onChange={() => handleSelectCase(Number(c.pkCase))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{c.caseNumber}</TableCell>
                        <TableCell>{c.orderType?.orderTypeName || "N/A"}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{`${c.client?.firstName || ""} ${c.client?.lastName || ""}`}</Typography>
                          <Typography variant="caption" color="textSecondary">{c.client?.email}</Typography>
                        </TableCell>
                        <TableCell>{new Date(c.dateCreated).toLocaleDateString()}</TableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right Side: Assignment Control Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={3} sx={{ borderRadius: 3, borderTop: `4px solid ${ColorData.primary}` }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <SupervisorAccountIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Assignment Controls
                </Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" mb={3}>
                Select one or more unassigned cases from the list, select a target admin user, and click Assign.
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                {/* Cases Selected Summary */}
                <Box bgcolor="#f8fafc" p={2} borderRadius={2} border="1px dashed #cbd5e1">
                  <Typography variant="caption" display="block" color="textSecondary" fontWeight="bold">
                    Cases Selected
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {selectedCaseIds.length}
                  </Typography>
                </Box>

                {/* Admin Select dropdown */}
                <FormControl fullWidth size="small">
                  <InputLabel id="admin-select-label">Select Target Admin</InputLabel>
                  <Select
                    labelId="admin-select-label"
                    value={selectedAdminId}
                    label="Select Target Admin"
                    onChange={(e) => setSelectedAdminId(e.target.value as number)}
                  >
                    <MenuItem value="">
                      <em>-- Select Admin --</em>
                    </MenuItem>
                    {admins.map((adm) => (
                      <MenuItem key={adm.id} value={adm.id}>
                        {`${adm.firstName} ${adm.lastName} (${adm.email})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Submit button */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!selectedAdminId || selectedCaseIds.length === 0}
                  onClick={handleAssign}
                  sx={{ py: 1.25, fontWeight: "bold" }}
                >
                  Assign Admin
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Result feedback dialogue */}
      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", borderBottom: "1px solid #e2e8f0" }}>
          Assignment Results
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={1.5}>
            {assignmentResults.map((r, index) => {
              const caseItem = cases.find(c => Number(c.pkCase) === Number(r.caseId));
              return (
                <Box
                  key={index}
                  p={1.5}
                  borderRadius={1.5}
                  border={r.success ? "1px solid #bbf7d0" : "1px solid #fecaca"}
                  bgcolor={r.success ? "#f0fdf4" : "#fdf2f2"}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {caseItem?.caseNumber || `Case ID: ${r.caseId}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {r.message}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={r.success ? "Assigned" : "Failed"}
                    color={r.success ? "success" : "error"}
                    icon={r.success ? <CheckCircleIcon fontSize="small" /> : undefined}
                    sx={{ fontWeight: "bold" }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0", p: 2 }}>
          <Button onClick={() => setResultDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkAdminAssignment;
